import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import EnvStore from '../../../stores/EnvStore';
import { ModalMode } from '../../../stores/Policy/PolicyModalStore';
import PolicyStore from '../../../stores/Policy/PolicyStore';
import ConsoleStore from '../../../stores/ConsoleStore';
import CheckboxContainer from '../../CheckboxContainer/CheckboxContainer';
import { FTBody, FTLabel } from '../../Fuse';
import Card from '../../Fuse/Card';
import FuseAlert from '../../FuseControls/FuseAlert';
import FuseCheckbox from '../../FuseControls/FuseCheckbox';
import FuseReadOnlyTextfield from '../../FuseControls/FuseReadOnlyTextfield';
import FuseReadOnlySwitch from '../../FuseControls/FuseReadOnlySwitch';
import PolicyModal from '../PolicyModal';
import PolicyModalHeaderLayout from '../PolicyModalHeaderLayout';
import UserGroups from './UserGroups';
import BrowserIsolationOptions from './BrowserIsolationOptions';
import UrlIsolationOptions from './UrlIsolationOptions';
import BrowsingRoleName from './BrowsingRoleName';
import { ROLE_READ_ONLY } from '../../../stores/Administrators/AdministratorModalStore';
import UrlIsolationOptionsStore from '../../../stores/Policy/BrowsingRoles/UrlIsolationOptionsStore';
import './BrowsingRoleModal.css';

const block = b.lock('BrowsingRoleModal');

class BrowsingRoleModal extends Component {
    static propTypes = {
        policy: PolicyStore.PropType.isRequired,
        env: EnvStore.PropType.isRequired,
        console: ConsoleStore.PropType,
    };

    componentWillMount() {
        // It is not ideal to always do this. Might be worth moving into the openModal method.
        // Not a huge deal since these are relatively lightweight but still.
        this.props.policy.contentRules.policyTable.load();
        this.props.policy.exclusionRules.policyTable.load();
        if (this.props.console.isFTREnabled()) {
            this.props.policy.fileThreatRules.policyTable.load();
        }
        if (this.props.console.isDLPEnabled()) {
            this.props.policy.dlpRules.policyTable.load();
        }
    }

    renderDeletePills = (arr) =>
        (arr.length > 0 ? arr.map((rule) => {
            return <FTLabel key={rule.id} className={block('DeleteConfirmPill')}> {rule.name} </FTLabel>;
        }) : <FTLabel>- None -</FTLabel>);

    confirmDeleteMessage = (flagStore) => {
        const item = this.props.policy.browsingRoles.policyTable.policyModal.getValue();
        const containsBrowsingRole = (entity) =>
            entity.browsingRoles.some(({ id }) => id === item.id);
        const ftRules = this.props.console.isFTREnabled()
            ? this.props.policy.fileThreatRules.policyTable.items.filter(containsBrowsingRole)
            : null;
        const dlpRules = this.props.console.isDLPEnabled()
            ? this.props.policy.dlpRules.policyTable.items.filter(containsBrowsingRole)
            : null;
        const cRules = this.props.policy.contentRules.policyTable.items.filter(containsBrowsingRole);
        const eRules = this.props.policy.exclusionRules.policyTable.items.filter(containsBrowsingRole);

        return <FuseAlert
            type="danger"
            title="Delete Confirmation"
        >
            <FTBody className={block('DeleteConfirmHeader')}>
                Browsing roles cannot be restored once deleted.
                This browsing role will be removed from all referenced rules.
            </FTBody>
            <FTLabel className={block('DeleteConfirmLabel')}>
                Referenced Exclusion Rules
            </FTLabel>
            <FTLabel>
                {this.renderDeletePills(eRules)}
            </FTLabel>
            <FTLabel className={block('DeleteConfirmLabel')}>
                Referenced Content Rules
            </FTLabel>
            <div>
                {this.renderDeletePills(cRules)}
            </div>
            { this.props.console.isFTREnabled() ?
                <>
                    <FTLabel className={block('DeleteConfirmLabel')}>
                        Referenced File Threat Rules
                    </FTLabel>
                    <div>
                        {this.renderDeletePills(ftRules)}
                    </div>
                </>
                : null }
            { this.props.console.isDLPEnabled() ?
                <>
                    <FTLabel className={block('DeleteConfirmLabel')}>
                        Referenced File DLP Rules
                    </FTLabel>
                    <div>
                        {this.renderDeletePills(dlpRules)}
                    </div>
                </>
                : null }
            <FuseCheckbox
                className={block('DeleteCheckbox')}
                black
                store={flagStore}
                label={'Confirm the deletion of this browsing role'}
                qa={'confirm-delete'}
            />
        </FuseAlert>;
    };

    renderHeader = () => {
        const { env, policy } = this.props;
        const { policyModal } = policy.browsingRoles.policyTable;
        const store = policyModal.header;
        const readOnly = policyModal.mode === ModalMode.VIEW || policyModal.mode === ModalMode.DELETE;
        const footer = store.isDefault
            ? 'The Default Browsing Role is system generated and cannot be deleted, renamed, or disabled.'
            : null;

        return <PolicyModalHeaderLayout
            title="Summary"
            readOnly={readOnly || store.isDefault}
            footer={footer}
        >
            <FuseReadOnlyTextfield
                label="Name"
                store={store.name}
                readOnlyValue={<BrowsingRoleName isDefault={store.isDefault} name={store.name.value} />}
                placeholder="Enter role name"
                error={!store.name.isValid && store.name.isTouched}
                onBlur={store.onNameFieldFocusChange}
                showRequired={!readOnly}
                required={!readOnly}
                qa="entity-name"
            />

            <FuseReadOnlyTextfield
                label="Description"
                store={store.description}
                readOnlyValue={store.description.value && store.description.value.trim() || '—'}
                placeholder="Enter role description"
                qa="entity-description"
            />

            <FuseReadOnlySwitch
                header="Role Status"
                tooltip="The status will determine if the Role is available to live users or not."
                tooltipProps={env.screen.isMobile || readOnly ? { position: 'right' } : {}}
                store={store.status}
                label={store.status.isTrue ? 'Enabled' : 'Disabled'}
                qa="status-switch"
            />
        </PolicyModalHeaderLayout>;
    };

    getSubTitle = () => {
        const { policyModal } = this.props.policy.browsingRoles.policyTable;
        return policyModal.mode === ModalMode.EDIT_CONFIRM
            ? '- Assigned User Emails'
            : `- ${policyModal.header.name.value || 'Unnamed'}`;
    }

    confirmEditMessage = (store) =>
        <Card title="Confirm Changes">
            <FTBody>Review changes to the Email Addresses before confirming.</FTBody>
            <CheckboxContainer className={block('ConfirmChangesCheckboxContainer')}>
                <FuseCheckbox
                    label={'Confirm changes to Email Addresses'}
                    store={store}
                    qa={'confirm-edit'}
                />
            </CheckboxContainer>
        </Card>;

    render() {
        const { console, policy } = this.props;
        const { policyModal } = policy.browsingRoles.policyTable;
        const { userGroups, browserIsolationOptions } = policyModal.sections;
        const readOnly = policyModal.mode === ModalMode.VIEW || policyModal.mode === ModalMode.DELETE;
        const editConfirm = policyModal.mode === ModalMode.EDIT_CONFIRM;
        const hasBrowserEmailLicense = console.hasBrowserOrEmailLicense();

        return <PolicyModal
            titleItemName="Browsing Role"
            itemName="Role"
            store={policyModal}
            readOnly={console.authority === ROLE_READ_ONLY.value}
            confirmDeleteMessage={this.confirmDeleteMessage}
            confirmEditMessage={this.confirmEditMessage}
            subTitle={this.getSubTitle()}
        >
            {!editConfirm
                ? this.renderHeader()
                : null
            }

            {policyModal.mode ? <UserGroups store={userGroups} mode={policyModal.mode} /> : null}

            {policyModal.mode !== ModalMode.EDIT_CONFIRM && hasBrowserEmailLicense ? <Fragment>
                {/* TODO: This bit disabled since we're not working on it yet */}
                {/* <GeographicOptions store={geographicOptions} /> */}
                <BrowserIsolationOptions
                    readOnly={readOnly}
                    store={browserIsolationOptions}
                    isSSOEnabled={console.isSSOEnabled}
                />

                <UrlIsolationOptions store={new UrlIsolationOptionsStore()} />
            </Fragment> : null}
        </PolicyModal>;
    }
}


export default inject('env', 'policy', 'console')(observer(BrowsingRoleModal));
